import { Insight, Episode, Podcast } from '../../types/index';
import { Card } from '../ui/card';
import { Quote, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface EnhancedInsight extends Insight {
  episode: Episode;
  podcast: Podcast;
}

interface InsightCardProps {
  insight: EnhancedInsight;
  className?: string;
}

export function InsightCard({ insight, className }: InsightCardProps) {
  const navigate = useNavigate();

  // Smart content handling for quotes
  const getDisplayQuote = () => {
    if (insight.supporting_quotes && insight.supporting_quotes.length > 0) {
      return insight.supporting_quotes[0];
    }
    // Fallback: Use first sentence or portion of description as a "key insight"
    const firstSentence = insight.description.split('.')[0] + '.';
    return firstSentence.length > 150 ? firstSentence.substring(0, 150) + '...' : firstSentence;
  };

  const hasRealQuote = insight.supporting_quotes && insight.supporting_quotes.length > 0;

  return (
    <Card className={`bg-white rounded-xl shadow-lg p-6 transition-all duration-200 hover:shadow-xl h-full flex flex-col ${className}`}>
      {/* Title Section - Fixed Height */}
      <div className="flex items-start gap-3 mb-4 min-h-[60px]">
        <div className="flex-shrink-0 mt-1">
          <Quote size={24} className="text-blue-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 leading-tight line-clamp-2">{insight.title}</h3>
      </div>

      {/* Content Section - More text display */}
      <div className="mb-4 flex-grow">
        <p className="text-gray-600 line-clamp-4 leading-relaxed">{insight.description}</p>
      </div>

      {/* Quote Section - Always Present with More Content */}
      <div className="mb-4 p-3 bg-gray-50 rounded-lg min-h-[90px] flex flex-col justify-center">
        <h4 className="font-medium text-gray-700 mb-2 text-sm">
          {hasRealQuote ? 'Key Quote:' : 'Key Insight:'}
        </h4>
        <p className="text-gray-600 italic text-sm line-clamp-3">
          "{getDisplayQuote()}"
        </p>
      </div>

      {/* Episode Info Section - Fixed Position at Bottom */}
      <div className="border-t border-gray-100 pt-4 mt-auto">
        <p className="text-sm text-gray-500 mb-1">From episode:</p>
        <p className="text-sm font-medium text-gray-700 line-clamp-1 mb-3">{insight.episode.title}</p>
        <div className="flex items-center justify-between">
          <p className="text-xs text-gray-500">{insight.podcast.channel_name}</p>
          
          {/* View Episode Link */}
          <button 
            className="flex items-center text-blue-600 hover:text-blue-700 font-medium transition-colors text-sm hover:bg-blue-50 px-2 py-1 rounded-md"
            onClick={() => navigate(`/episode/${insight.episode.id}#insight-${insight.id}`)}
          >
            View episode
            <ChevronRight size={16} className="ml-1" />
          </button>
        </div>
      </div>
    </Card>
  );
} 